import { Controller } from "@hotwired/stimulus"
import { Marked } from "marked"
import { markedHighlight } from "marked-highlight"
import hljs from "highlight.js/lib/common"
import DOMPurify from "dompurify"
import "highlight.js/styles/atom-one-light.css"
import "./markdown_editor_component.css"

const SAFE_LINK_PROTOCOL = /^(https?:|mailto:|\/|#)/i
const INTERNAL_ATTACHMENT = /^\/images\/\d+/
const TASK_URL =
  /^(?:https?:\/\/([^/\s]+))?(?:\/projects)?\/\d+-[^/\s]*\/task\/([^/?#\s]+)\/?(?:[?#].*)?$/
const TASK_STATUS_ICONS = {
  todo: "fa-regular fa-circle text-sapphire",
  in_progress: "fa-solid fa-circle-half-stroke text-warning",
  completed: "fa-solid fa-check text-success",
  cancelled: "fa-solid fa-xmark text-danger"
}

const taskRefCache = new Map()

const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[
        char
      ])
  )

const isOwnHost = (host) => {
  const hostname = host.split(":")[0]
  const base = window.location.hostname.replace(/^projects\./, "")
  return hostname === base || hostname.endsWith(`.${base}`)
}

const taskRefFor = (href, text) => {
  const match = href.match(TASK_URL)
  if (!match) return null

  const [, host, encodedRef] = match
  if (host && !isOwnHost(host)) return null

  const ref = decodeURIComponent(encodedRef)
  return text === href || text === ref ? ref : null
}

let checkboxIndex = 0

const checkboxHtml = (checked) =>
  `<input type="checkbox" ${checked ? "checked" : ""} data-checkbox-index="${checkboxIndex++}" class="cursor-pointer"> `

const taskListExtension = {
  name: "taskListNoMarker",
  level: "inline",
  start(src) {
    return src.match(/^\[[ x]\]/)?.index
  },
  tokenizer(src) {
    const match = src.match(/^\[([ x])\]\s*/)
    if (match) {
      return {
        type: "taskListNoMarker",
        raw: match[0],
        checked: match[1] === "x"
      }
    }
  },
  renderer(token) {
    return checkboxHtml(token.checked)
  }
}

const renderer = {
  checkbox({ checked }) {
    return checkboxHtml(checked)
  },

  link({ href, title, text }) {
    const safeHref = SAFE_LINK_PROTOCOL.test(href) ? href : "#"
    const titleAttr = title ? ` title="${title}"` : ""

    const taskRef = taskRefFor(href, text)
    if (taskRef) {
      const chipHref = escapeHtml(safeHref)
      const chipRef = escapeHtml(taskRef)
      return (
        `<a href="${chipHref}" class="task-ref-chip" data-task-ref="${chipRef}" data-turbo-frame="_top">` +
        `<i class="task-ref-status fa-regular fa-circle"></i>` +
        `<span class="task-ref-id">${chipRef}</span>` +
        `<span class="task-ref-title"></span></a>`
      )
    }

    if (INTERNAL_ATTACHMENT.test(href)) {
      return (
        `<a href="${safeHref}"${titleAttr} target="_blank" rel="noopener noreferrer" class="attachment-chip">` +
        `<i class="fa-regular fa-file"></i>${text}</a>`
      )
    }

    return `<a href="${safeHref}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`
  },

  image({ href, title, text }) {
    const safeSrc = SAFE_LINK_PROTOCOL.test(href) ? href : ""
    const titleAttr = title ? ` title="${title}"` : ""
    const img = `<img src="${safeSrc}" alt="${text || ""}"${titleAttr} loading="lazy">`

    if (INTERNAL_ATTACHMENT.test(href)) {
      return `<a href="${safeSrc}" target="_blank" rel="noopener noreferrer">${img}</a>`
    }

    return img
  }
}

const marked = new Marked(
  markedHighlight({
    langPrefix: "hljs language-",
    highlight(code, lang) {
      const language = hljs.getLanguage(lang) ? lang : "plaintext"
      return hljs.highlight(code, { language }).value
    }
  }),
  {
    gfm: true,
    breaks: true,
    async: false,
    renderer
  }
)

const taskMentionExtension = {
  name: "taskMention",
  level: "inline",
  start(src) {
    const match = src.match(/(^|[^\w/-])[A-Z][A-Z0-9]*-\d+(?![\w-])/)
    return match ? match.index + match[1].length : undefined
  },
  tokenizer(src) {
    const match = src.match(/^([A-Z][A-Z0-9]*-\d+)(?![\w-])/)
    if (match) {
      return { type: "taskMention", raw: match[0], ref: match[1] }
    }
  },
  renderer(token) {
    return `<span class="task-ref-mention" data-task-ref="${token.ref}">${token.ref}</span>`
  }
}

marked.use({ extensions: [taskListExtension, taskMentionExtension] })

export default class extends Controller {
  static targets = [
    "preview",
    "editor",
    "input",
    "saveButton",
    "status",
    "toggleButton",
    "toggleLabel",
    "writeTab",
    "previewTab",
    "help",
    "dropZone"
  ]
  static values = { autoGrow: Boolean, uploadUrl: String, taskRefsUrl: String }

  connect() {
    this.render()
    this.hideStatusTimeout = null
    this.updateToggle()

    // Cmd/Ctrl+E toggles edit/view from anywhere on the page.
    this.boundGlobalKeydown = this.handleGlobalKeydown.bind(this)
    window.addEventListener("keydown", this.boundGlobalKeydown)

    // Find parent form and listen for turbo events
    this.form = this.element.closest("form")
    if (this.form) {
      this.boundOnSubmitStart = this.onSubmitStart.bind(this)
      this.boundOnSubmitEnd = this.onSubmitEnd.bind(this)
      this.form.addEventListener("turbo:submit-start", this.boundOnSubmitStart)
      this.form.addEventListener("turbo:submit-end", this.boundOnSubmitEnd)
    }
  }

  disconnect() {
    if (this.hideStatusTimeout) {
      clearTimeout(this.hideStatusTimeout)
    }
    window.removeEventListener("keydown", this.boundGlobalKeydown)
    if (this.form) {
      this.form.removeEventListener(
        "turbo:submit-start",
        this.boundOnSubmitStart
      )
      this.form.removeEventListener("turbo:submit-end", this.boundOnSubmitEnd)
    }
  }

  get editing() {
    return !this.editorTarget.classList.contains("hidden")
  }

  toggleMode() {
    this.editing ? this.showView() : this.showEdit()
  }

  handleGlobalKeydown(event) {
    if (event.key !== "e" || !(event.metaKey || event.ctrlKey)) return
    // Ignore when this editor isn't visible on the page.
    if (!this.element.offsetParent) return

    // In view mode, don't hijack Cmd+E if the user is typing in another field.
    const active = document.activeElement
    const typingElsewhere =
      active &&
      active !== document.body &&
      !this.element.contains(active) &&
      (active.tagName === "INPUT" ||
        active.tagName === "TEXTAREA" ||
        active.isContentEditable)
    if (!this.editing && typingElsewhere) return

    event.preventDefault()
    this.toggleMode()
  }

  setStatus(html) {
    this.statusTargets.forEach((target) => {
      target.innerHTML = html
    })
  }

  onSubmitStart() {
    if (this.hideStatusTimeout) {
      clearTimeout(this.hideStatusTimeout)
    }
    this.submitStartTime = Date.now()
    this.setStatus(`
      <span class="text-xs text-gray-400 flex items-center gap-1">
        <svg class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Saving...
      </span>
    `)
  }

  onSubmitEnd(event) {
    const success = event.detail?.success !== false
    const elapsed = Date.now() - this.submitStartTime
    const minDisplayTime = 1000
    const remainingTime = Math.max(0, minDisplayTime - elapsed)

    setTimeout(() => {
      if (success) {
        this.setStatus(`
          <span class="text-xs text-green-600 flex items-center gap-1">
            <i class="fa-solid fa-check"></i>
            Saved
          </span>
        `)
        this.hideStatusTimeout = setTimeout(() => {
          this.setStatus("")
        }, 5000)
      } else {
        this.setStatus(`<span class="text-xs text-red-600">Error saving</span>`)
      }
    }, remainingTime)
  }

  toggleCheckbox(event) {
    if (event.target.type !== "checkbox") return

    const index = parseInt(event.target.dataset.checkboxIndex)
    const markdown = this.inputTarget.value

    // Find and toggle the nth checkbox in markdown
    let count = 0
    const newMarkdown = markdown.replace(/\[([ xX])\]/g, (match, state) => {
      if (count++ === index) {
        return state === " " ? "[x]" : "[ ]"
      }
      return match
    })

    this.inputTarget.value = newMarkdown
    this.render()

    // Trigger auto-submit
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  showEdit() {
    if (this.editing) return

    this._originalValue = this.inputTarget.value
    // Capture preview height/scroll before hiding to keep size and position.
    const previewHeight = this.previewTarget.offsetHeight
    const scrollTop = this.previewTarget.scrollTop

    this.previewTarget.classList.add("hidden")
    this.editorTarget.classList.remove("hidden")
    this.editorTarget.style.minHeight = `${previewHeight}px`

    // preventScroll keeps the page from jumping to the textarea's caret.
    this.inputTarget.focus({ preventScroll: true })
    this.autoGrow()
    this.inputTarget.scrollTop = scrollTop
    this.updateToggle()
  }

  showView() {
    if (!this.editing) return
    this.save()
  }

  // Grow the textarea to fit its content (full-screen views only). In the modal
  // the textarea fills a fixed-height, scrollable area instead.
  autoGrow() {
    if (!this.autoGrowValue) return

    const textarea = this.inputTarget
    textarea.style.height = "auto"
    textarea.style.height = `${textarea.scrollHeight}px`
  }

  save() {
    const scrollTop = this.inputTarget.scrollTop
    this.render()
    this.editorTarget.style.minHeight = ""
    this.editorTarget.classList.add("hidden")
    this.previewTarget.classList.remove("hidden")
    this.previewTarget.scrollTop = scrollTop
    this.updateToggle()

    // Trigger auto-submit
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // The single toggle button advertises the mode it switches *to*. The keybinds
  // help (?) is only relevant while editing, so it's hidden in view mode.
  updateToggle() {
    const editing = this.editing

    if (this.hasToggleLabelTarget) {
      this.toggleLabelTarget.textContent = editing ? "preview" : "edit"
    }
    if (this.hasWriteTabTarget) {
      this.writeTabTarget.setAttribute("aria-selected", editing)
    }
    if (this.hasPreviewTabTarget) {
      this.previewTabTarget.setAttribute("aria-selected", !editing)
    }
    if (this.hasHelpTarget) {
      this.helpTarget.classList.toggle("hidden", !editing)
      this.helpTarget.classList.toggle("flex", editing)
    }
  }

  render() {
    checkboxIndex = 0
    let markdown = this.inputTarget.value || ""
    if (markdown.trim()) {
      this.previewTarget.innerHTML = DOMPurify.sanitize(
        marked.parse(markdown),
        {
          ADD_ATTR: [
            "target",
            "data-checkbox-index",
            "data-task-ref",
            "data-turbo-frame",
            "loading"
          ]
        }
      )
      this.normalizeImageDimensions()
      this.hydrateTaskRefs()
    } else {
      this.previewTarget.innerHTML =
        '<span class="text-gray-400">No description</span>'
    }
  }

  async hydrateTaskRefs() {
    const chips = Array.from(
      this.previewTarget.querySelectorAll("[data-task-ref]")
    )
    if (!chips.length || !this.taskRefsUrlValue) return

    const refs = [...new Set(chips.map((chip) => chip.dataset.taskRef))]
    const missing = refs.filter((ref) => !taskRefCache.has(ref))

    if (missing.length) {
      const url = new URL(this.taskRefsUrlValue, window.location.origin)
      url.searchParams.set("ids", missing.join(","))
      const request = fetch(url, { headers: { Accept: "application/json" } })
        .then((response) => (response.ok ? response.json() : {}))
        .catch(() => ({}))
      missing.forEach((ref) => {
        taskRefCache.set(
          ref,
          request.then((data) => data[ref] || null)
        )
      })
    }

    const resolved = await Promise.all(refs.map((ref) => taskRefCache.get(ref)))
    const byRef = Object.fromEntries(
      refs.map((ref, index) => [ref, resolved[index]])
    )

    chips.forEach((element) => {
      const task = byRef[element.dataset.taskRef]
      if (!task || !element.isConnected) return

      if (element.tagName === "A") {
        this.fillTaskChip(element, task)
      } else if (!element.closest("a")) {
        element.replaceWith(this.buildTaskChip(task))
      }
    })
  }

  buildTaskChip(task) {
    const chip = document.createElement("a")
    chip.className = "task-ref-chip"
    chip.dataset.taskRef = task.identifier
    chip.dataset.turboFrame = "_top"

    const status = document.createElement("i")
    status.className = "task-ref-status"
    const identifier = document.createElement("span")
    identifier.className = "task-ref-id"
    const title = document.createElement("span")
    title.className = "task-ref-title"

    chip.append(status, identifier, title)
    this.fillTaskChip(chip, task)
    return chip
  }

  fillTaskChip(chip, task) {
    chip.href = task.url
    chip.title = task.title || ""
    chip.querySelector(".task-ref-id").textContent = task.identifier
    chip.querySelector(".task-ref-title").textContent = task.title || "Untitled"
    chip.querySelector(".task-ref-status").className =
      `task-ref-status ${TASK_STATUS_ICONS[task.status] || TASK_STATUS_ICONS.todo}`
    chip.classList.toggle(
      "task-ref-chip--closed",
      ["completed", "cancelled"].includes(task.status)
    )
  }

  normalizeImageDimensions() {
    this.previewTarget.querySelectorAll("img[width]").forEach((img) => {
      const value = img.getAttribute("width")
      if (!value) return

      img.style.width = /^\d+$/.test(value) ? `${value}px` : value
      img.style.height = "auto"
    })
  }

  handlePaste(event) {
    if (!this.hasUploadUrlValue) return

    const files = event.clipboardData?.files
    if (!files || files.length === 0) return

    event.preventDefault()
    this.uploadFiles(files)
  }

  handleDrop(event) {
    if (!this.hasUploadUrlValue) return
    this.hideDropZone()

    const files = event.dataTransfer?.files
    if (!files || files.length === 0) return

    event.preventDefault()
    this.uploadFiles(files)
  }

  handleDragover(event) {
    if (!this.hasUploadUrlValue) return
    if (!event.dataTransfer?.types?.includes("Files")) return

    event.preventDefault()
    this.showDropZone()
  }

  handleDragleave() {
    this.hideDropZone()
  }

  showDropZone() {
    if (!this.hasDropZoneTarget) return
    this.dropZoneTarget.classList.remove("hidden")
    this.dropZoneTarget.classList.add("flex")
  }

  hideDropZone() {
    if (!this.hasDropZoneTarget) return
    this.dropZoneTarget.classList.add("hidden")
    this.dropZoneTarget.classList.remove("flex")
  }

  uploadFiles(fileList) {
    Array.from(fileList).forEach((file) => this.uploadFile(file))
  }

  uploadFile(file) {
    const placeholder = this.insertPlaceholder(file.name)
    const sizePromise = this.readImageSize(file)

    const formData = new FormData()
    formData.append("file", file)

    fetch(this.uploadUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          ?.content,
        Accept: "application/json"
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error("Upload failed")
        return response.json()
      })
      .then((data) =>
        sizePromise.then((size) =>
          this.replacePlaceholder(
            placeholder,
            this.attachmentReference(data, size)
          )
        )
      )
      .catch(() => {
        this.replacePlaceholder(placeholder, "")
        window.dispatchEvent(
          new CustomEvent("toast:show", {
            detail: { message: `Upload failed: ${file.name}`, type: "error" }
          })
        )
      })
  }

  attachmentReference(data, size) {
    if (!data.content_type?.startsWith("image/")) return data.markdown

    const alt = data.filename.replace(/\.[^./\\]+$/, "").replace(/"/g, "")
    const dims = size ? ` width="${size.width}" height="${size.height}"` : ""
    return `<img alt="${alt}" src="${data.url}"${dims} loading="lazy">`
  }

  readImageSize(file) {
    return new Promise((resolve) => {
      if (!file.type?.startsWith("image/")) return resolve(null)

      const url = URL.createObjectURL(file)
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(url)
        resolve({ width: img.naturalWidth, height: img.naturalHeight })
      }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        resolve(null)
      }
      img.src = url
    })
  }

  handlePreviewClick(event) {
    if (event.target.tagName !== "IMG") return

    event.preventDefault()
    this.openLightbox(event.target.src)
  }

  openLightbox(src) {
    const overlay = document.createElement("div")
    overlay.className =
      "fixed inset-0 z-50 flex items-center justify-center bg-black/80 cursor-zoom-out p-8"

    const img = document.createElement("img")
    img.src = src
    img.className = "max-w-full max-h-full rounded shadow-lg"
    overlay.appendChild(img)

    const close = () => {
      overlay.remove()
      document.removeEventListener("keydown", onKey)
    }
    const onKey = (event) => {
      if (event.key === "Escape") close()
    }

    overlay.addEventListener("click", close)
    document.addEventListener("keydown", onKey)
    document.body.appendChild(overlay)
  }

  insertPlaceholder(name) {
    const textarea = this.inputTarget
    const base = `![Uploading ${name}…]()`

    let placeholder = base
    let counter = 2
    while (textarea.value.includes(placeholder)) {
      placeholder = `![Uploading ${name}… (${counter++})]()`
    }

    textarea.setRangeText(
      placeholder,
      textarea.selectionStart,
      textarea.selectionEnd,
      "end"
    )
    this.autoGrow()
    return placeholder
  }

  replacePlaceholder(placeholder, markdown) {
    const textarea = this.inputTarget
    const index = textarea.value.indexOf(placeholder)
    if (index === -1) return

    textarea.setRangeText(markdown, index, index + placeholder.length)
    this.render()
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this.save()
    } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      event.stopPropagation()
      this.save()
    } else if (event.key === "Tab" && !event.ctrlKey && !event.metaKey) {
      this.handleTab(event)
    } else if (event.key === "b" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      event.stopPropagation()
      this.wrapSelection("**")
    } else if (event.key === "i" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      event.stopPropagation()
      this.wrapSelection("_")
    } else if (event.key === "k" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      event.stopPropagation()
      this.insertLink()
    } else if (event.key === "t" && event.ctrlKey) {
      this.insertCheckbox(event)
    } else if (event.key === "Enter") {
      this.smartNewLine(event)
    }
  }

  handleTab(event) {
    const textarea = event.target
    const cursorPos = textarea.selectionStart
    const text = textarea.value
    const lineStart = text.lastIndexOf("\n", cursorPos - 1) + 1
    const lineEnd = text.indexOf("\n", cursorPos)
    const currentLine = text.slice(
      lineStart,
      lineEnd === -1 ? text.length : lineEnd
    )

    if (!currentLine.match(/^\s*[-*+]\s+/)) return

    event.preventDefault()

    if (event.shiftKey) {
      const spaces = currentLine.match(/^(\s*)/)[1].length
      const removeCount = Math.min(2, spaces)
      if (removeCount > 0) {
        textarea.setRangeText("", lineStart, lineStart + removeCount, "end")
      }
    } else {
      textarea.setRangeText("  ", lineStart, lineStart, "end")
    }
  }

  wrapSelection(wrapper) {
    const textarea = this.inputTarget
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = textarea.value
    const len = wrapper.length

    const before = text.slice(start - len, start)
    const after = text.slice(end, end + len)
    const selected = text.slice(start, end)

    if (before === wrapper && after === wrapper) {
      textarea.setRangeText(selected, start - len, end + len)
      textarea.selectionStart = start - len
      textarea.selectionEnd = end - len
    } else if (
      selected.startsWith(wrapper) &&
      selected.endsWith(wrapper) &&
      selected.length >= len * 2
    ) {
      const inner = selected.slice(len, -len)
      textarea.setRangeText(inner, start, end)
      textarea.selectionStart = start
      textarea.selectionEnd = start + inner.length
    } else if (selected) {
      textarea.setRangeText(`${wrapper}${selected}${wrapper}`, start, end)
      textarea.selectionStart = start + len
      textarea.selectionEnd = end + len
    } else {
      textarea.setRangeText(`${wrapper}${wrapper}`, start, end)
      textarea.selectionStart = start + len
      textarea.selectionEnd = start + len
    }
    textarea.focus()
  }

  insertLink() {
    const textarea = this.inputTarget
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = textarea.value
    const selected = text.slice(start, end)

    const linkMatch = selected.match(/^\[(.+?)\]\((.+?)\)$/)
    if (linkMatch) {
      textarea.setRangeText(linkMatch[1], start, end)
      textarea.selectionStart = start
      textarea.selectionEnd = start + linkMatch[1].length
    } else if (selected) {
      textarea.setRangeText(`[${selected}](url)`, start, end)
      textarea.selectionStart = end + 3
      textarea.selectionEnd = end + 6
    } else {
      textarea.setRangeText("[text](url)", start, end)
      textarea.selectionStart = start + 1
      textarea.selectionEnd = start + 5
    }
    textarea.focus()
  }

  insertCheckbox(event) {
    const textarea = event.target
    const cursorPos = textarea.selectionStart
    const text = textarea.value
    const lineStart = text.lastIndexOf("\n", cursorPos - 1) + 1
    const lineEnd = text.indexOf("\n", cursorPos)
    const currentLineEnd = lineEnd === -1 ? text.length : lineEnd
    const currentLine = text.slice(lineStart, currentLineEnd)

    const checkboxMatch = currentLine.match(/^(\s*)\[[ x]\]\s?/)
    if (checkboxMatch) {
      event.preventDefault()
      const replacement = checkboxMatch[1]
      textarea.setRangeText(
        replacement,
        lineStart,
        lineStart + checkboxMatch[0].length,
        "end"
      )
    } else if (currentLine.trim() === "") {
      event.preventDefault()
      const indent = currentLine.match(/^(\s*)/)[1]
      textarea.setRangeText(`${indent}[ ] `, lineStart, currentLineEnd, "end")
    }
  }

  // Continue list markers on Enter (like GitHub). Empty markers are removed.
  smartNewLine(event) {
    const textarea = event.target
    const cursorPos = textarea.selectionStart
    const text = textarea.value
    const lineStart = text.lastIndexOf("\n", cursorPos - 1) + 1
    const lineEnd = text.indexOf("\n", cursorPos)
    const currentLineEnd = lineEnd === -1 ? text.length : lineEnd

    // Only trigger smart behavior when cursor is at end of line
    if (text.slice(cursorPos, currentLineEnd).trim().length > 0) return

    const currentLine = text.slice(lineStart, cursorPos)

    // Match: optional indentation + marker (checkbox or bullet)
    const match = currentLine.match(/^(\s*)((\[[ x]\]\s*)|([-*+]\s+))/)
    if (!match) return

    const indent = match[1]
    const marker = match[2]

    // Normalize [x] to [ ] for new items
    const normalizedMarker = marker.replace("[x]", "[ ]")

    event.preventDefault()

    // If line is only the marker (empty list item), remove it
    if (currentLine.trim() === marker.trim()) {
      textarea.setRangeText("", lineStart, cursorPos, "end")
    } else {
      textarea.setRangeText(
        `\n${indent}${normalizedMarker}`,
        cursorPos,
        cursorPos,
        "end"
      )
    }
  }
}
